import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiBell, FiCopy, FiEdit2, FiKey, FiPlus, FiPower, FiShield, FiSlash, FiVolume2, FiX } from "react-icons/fi";
import { QRCodeSVG } from "qrcode.react";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";

const DEVICE_TYPE_OPTIONS = [
  { value: "guest_order_tablet", label: "Guest ordering tablet" },
  { value: "service_display", label: "Service display" },
  { value: "pos_terminal", label: "POS terminal" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const ALERT_TONE_OPTIONS = [
  { value: "chime", label: "Chime" },
  { value: "bell", label: "Bell" },
  { value: "urgent", label: "Urgent" },
];

const ALERT_REPEAT_OPTIONS = [
  { value: 0, label: "Only when it arrives" },
  { value: 15, label: "Every 15 seconds" },
  { value: 30, label: "Every 30 seconds" },
  { value: 60, label: "Every minute" },
  { value: 120, label: "Every 2 minutes" },
];

const DEFAULT_ALERT_SETTINGS = {
  sound_enabled: true,
  visual_enabled: true,
  tone: "chime",
  volume: 80,
  repeat_seconds: 30,
};

const EMPTY_FORM = {
  name: "",
  outlet_id: "",
  outlet_ids: [],
  catalog_id: "",
  device_type: "guest_order_tablet",
  status: "active",
  alert_settings: DEFAULT_ALERT_SETTINGS,
};

function getBusinessUnitId(searchParams) {
  const user = readStoredUser();
  return searchParams.get("business_unit_id") || user?.active_business_unit_id || user?.business_unit_ids?.[0] || "1";
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function deviceTypeLabel(value) {
  return DEVICE_TYPE_OPTIONS.find((option) => option.value === value)?.label || "Guest ordering tablet";
}

function statusLabel(value) {
  return STATUS_OPTIONS.find((option) => option.value === value)?.label || "Active";
}

export default function Devices() {
  const [searchParams] = useSearchParams();
  const businessUnitId = getBusinessUnitId(searchParams);
  const [devices, setDevices] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modalMode, setModalMode] = useState(null);
  const [deviceForm, setDeviceForm] = useState(EMPTY_FORM);
  const [editingDeviceId, setEditingDeviceId] = useState(null);
  const [generatedAccess, setGeneratedAccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const limit = 50;

  const hasPrevious = offset > 0;
  const hasNext = offset + limit < count;

  const activeCount = useMemo(
    () => devices.filter((device) => device.status === "active").length,
    [devices]
  );

  const loadDevices = useCallback(async (nextOffset = 0) => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get("/api/pos/devices", {
        params: {
          business_unit_id: businessUnitId,
          q: debouncedSearchQuery,
          device_type: typeFilter,
          status: statusFilter,
          limit,
          offset: nextOffset,
        },
      });
      setDevices(res.data?.rows || []);
      setCount(res.data?.count || 0);
      setOffset(nextOffset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load POS devices.");
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, debouncedSearchQuery, statusFilter, typeFilter]);

  const loadSetupOptions = useCallback(async () => {
    try {
      const [catalogRes, outletRes] = await Promise.all([
        axios.get("/api/pos/catalogs/options", { params: { business_unit_id: businessUnitId } }),
        axios.get("/api/pos/outlets", { params: { business_unit_id: businessUnitId } }),
      ]);
      setCatalogs(catalogRes.data?.rows || []);
      setOutlets(outletRes.data?.options || []);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load POS device setup options.");
    }
  }, [businessUnitId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    loadDevices(0);
  }, [loadDevices]);

  useEffect(() => {
    loadSetupOptions();
  }, [loadSetupOptions]);

  const clearFilters = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setTypeFilter("");
    setStatusFilter("");
  };

  const openCreateModal = () => {
    setError("");
    setMessage("");
    setModalMode("create");
    setEditingDeviceId(null);
    setDeviceForm({ ...EMPTY_FORM, outlet_ids: [], alert_settings: { ...DEFAULT_ALERT_SETTINGS } });
  };

  const openEditModal = (device) => {
    setError("");
    setMessage("");
    setModalMode("edit");
    setEditingDeviceId(device.id);
    setDeviceForm({
      name: device.name || "",
      outlet_id: device.outlet_id || "",
      outlet_ids: Array.isArray(device.outlet_ids) ? device.outlet_ids : [],
      catalog_id: device.catalog_id || "",
      device_type: device.device_type || "guest_order_tablet",
      status: device.status || "active",
      alert_settings: { ...DEFAULT_ALERT_SETTINGS, ...(device.alert_settings || {}) },
    });
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingDeviceId(null);
    setDeviceForm({ ...EMPTY_FORM, outlet_ids: [], alert_settings: { ...DEFAULT_ALERT_SETTINGS } });
  };

  const updateForm = (field, value) => {
    setDeviceForm((current) => ({ ...current, [field]: value }));
  };

  const updateDeviceType = (deviceType) => {
    setDeviceForm((current) => ({
      ...current,
      device_type: deviceType,
      ...(deviceType === "service_display"
        ? { outlet_id: "", catalog_id: "" }
        : { outlet_ids: [] }),
    }));
  };

  const toggleDisplayOutlet = (outletId) => {
    setDeviceForm((current) => {
      const selected = new Set((current.outlet_ids || []).map(String));
      const key = String(outletId);
      if (selected.has(key)) selected.delete(key);
      else selected.add(key);
      return { ...current, outlet_ids: Array.from(selected) };
    });
  };

  const updateAlertSetting = (field, value) => {
    setDeviceForm((current) => ({
      ...current,
      alert_settings: { ...current.alert_settings, [field]: value },
    }));
  };

  const updateCatalog = (catalogId) => {
    const catalog = catalogs.find((row) => String(row.id) === String(catalogId));
    setDeviceForm((current) => ({
      ...current,
      catalog_id: catalogId,
      ...(catalog?.outlet_id ? { outlet_id: catalog.outlet_id } : {}),
    }));
  };

  const saveDevice = async () => {
    const name = deviceForm.name.trim();
    if (!name) {
      setError("Device name is required.");
      return;
    }
    if (deviceForm.device_type === "guest_order_tablet" && !deviceForm.catalog_id) {
      setError("Guest ordering tablets need a catalog.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setMessage("");
      const payload = {
        business_unit_id: businessUnitId,
        name,
        outlet_id: deviceForm.device_type === "service_display" ? null : deviceForm.outlet_id || null,
        outlet_ids: deviceForm.device_type === "service_display" ? deviceForm.outlet_ids : [],
        catalog_id: deviceForm.device_type === "guest_order_tablet" ? deviceForm.catalog_id || null : null,
        device_type: deviceForm.device_type,
        status: deviceForm.status,
        alert_settings: deviceForm.device_type === "service_display" ? deviceForm.alert_settings : undefined,
      };

      if (modalMode === "edit" && editingDeviceId) {
        await axios.patch(`/api/pos/devices/${editingDeviceId}`, payload);
        setMessage(`${name} updated.`);
        await Promise.all([loadDevices(offset), loadSetupOptions()]);
      } else {
        const res = await axios.post("/api/pos/devices", payload);
        if (res.data?.pairing_url || res.data?.access_url) {
          setGeneratedAccess({
            deviceName: res.data?.device?.name || name,
            deviceType: res.data?.device?.device_type || deviceForm.device_type,
            accessUrl: res.data?.pairing_url || res.data?.access_url || "",
            expiresAt: res.data?.pairing_expires_at || null,
          });
          setMessage(`${name} created. Scan the one-time QR on the device.`);
        } else {
          setMessage(`${name} created.`);
        }
        await Promise.all([loadDevices(0), loadSetupOptions()]);
      }

      closeModal();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not save POS device.");
    } finally {
      setSaving(false);
    }
  };

  const updateDeviceStatus = async (device) => {
    const nextStatus = device.status === "active" ? "inactive" : "active";
    try {
      setActionId(device.id);
      setError("");
      setMessage("");
      await axios.patch(`/api/pos/devices/${device.id}`, {
        business_unit_id: businessUnitId,
        status: nextStatus,
      });
      setMessage(`${device.name} marked as ${statusLabel(nextStatus).toLowerCase()}.`);
      await loadDevices(offset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not update POS device status.");
    } finally {
      setActionId(null);
    }
  };

  const createPairing = async (device) => {
    try {
      setActionId(device.id);
      setError("");
      setMessage("");
      const res = await axios.post(`/api/pos/devices/${device.id}/pairing`, {
        business_unit_id: businessUnitId,
      });
      setGeneratedAccess({
        deviceName: res.data?.device?.name || device.name,
        deviceType: res.data?.device?.device_type || device.device_type,
        accessUrl: res.data?.pairing_url || res.data?.access_url || "",
        expiresAt: res.data?.pairing_expires_at || null,
      });
      setMessage(`One-time pairing ready for ${device.name}.`);
      await loadDevices(offset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not prepare POS device pairing.");
    } finally {
      setActionId(null);
    }
  };

  const revokeDevice = async (device) => {
    if (!window.confirm(`Revoke access for ${device.name}? The device will stop working immediately.`)) return;
    try {
      setActionId(device.id);
      setError("");
      setMessage("");
      await axios.post(`/api/pos/devices/${device.id}/revoke`, {
        business_unit_id: businessUnitId,
      });
      setGeneratedAccess(null);
      setMessage(`${device.name} access revoked.`);
      await loadDevices(offset);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not revoke POS device access.");
    } finally {
      setActionId(null);
    }
  };

  const copyAccessUrl = async () => {
    if (!generatedAccess?.accessUrl) return;
    try {
      await navigator.clipboard.writeText(generatedAccess.accessUrl);
      setMessage("Access URL copied.");
    } catch (_err) {
      setError("Could not copy the URL automatically.");
    }
  };

  return (
    <div className="reservations-container rm-page">
      <div className="rm-toolbarCard">
        <div className="rm-toolbarRow">
          <div>
            <h3 className="rm-rates-title">POS devices</h3>
            <div className="rm-rates-subtitle">
              {loading ? "Loading devices..." : `${count} devices configured · ${activeCount} active on this page`}
            </div>
          </div>
          <div className="rm-topActions">
            <input
              className="rm-searchInput"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search device or outlet"
            />
            <select
              className="posSelect posDeviceToolbarSelect"
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
            >
              <option value="">All types</option>
              {DEVICE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <select
              className="posSelect posDeviceStatusSelect"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button type="button" className="rm-topBtn rm-topBtn--violet" onClick={clearFilters}>
              <FiX /> Clear
            </button>
            <button type="button" className="rm-topBtn rm-topBtn--green" onClick={openCreateModal}>
              <FiPlus /> Add device
            </button>
          </div>
        </div>
      </div>

      {error ? <div className="posStateBanner">{error}</div> : null}
      {message ? <div className="posInfoBanner">{message}</div> : null}

      <div className="rm-rates-card">
        <div className="rm-rates-header">
          <h3 className="rm-rates-title">Device routing</h3>
          <div className="rm-rates-subtitle">Assign catalogs to guest tablets and receiving outlets to service displays.</div>
        </div>

        <div className="res-listMetaRow rm-listMetaRow">
          <small className="res-listMetaText">
            {loading ? "Loading devices..." : `Showing ${count ? offset + 1 : 0}-${Math.min(count, offset + devices.length)} of ${count}`}
          </small>
          <div className="rm-topActions">
            <button type="button" className="rm-topBtn" disabled={!hasPrevious} onClick={() => loadDevices(Math.max(0, offset - limit))}>
              Previous
            </button>
            <button type="button" className="rm-topBtn" disabled={!hasNext} onClick={() => loadDevices(offset + limit)}>
              Next
            </button>
          </div>
        </div>

        <div className="rm-rates-table-wrap">
          <table className="rm-rates-table" style={{ "--rm-col1-width": "260px", "--rm-table-min-width": "1440px" }}>
            <colgroup>
              <col style={{ width: "260px" }} />
              <col style={{ width: "190px" }} />
              <col style={{ width: "230px" }} />
              <col style={{ width: "210px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "190px" }} />
              <col style={{ width: "170px" }} />
              <col style={{ width: "390px" }} />
            </colgroup>
            <thead>
              <tr>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Device</span><span className="res-thSub">Name</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Outlet</span><span className="res-thSub">Area</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Catalog</span><span className="res-thSub">Products shown</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Type</span><span className="res-thSub">Use case</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Status</span><span className="res-thSub">Access</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Pairing</span><span className="res-thSub">Device binding</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Last seen</span><span className="res-thSub">Activity</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Actions</span><span className="res-thSub">Setup</span></div></th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => (
                <tr key={device.id}>
                  <td>
                    <strong className="posDeviceName">{device.name}</strong>
                    <small className="posDeviceMeta">Updated {formatDate(device.updated_at)}</small>
                  </td>
                  <td>{device.outlet_name || "-"}</td>
                  <td>{device.catalog_name || "-"}</td>
                  <td>{device.device_type_label || deviceTypeLabel(device.device_type)}</td>
                  <td>
                    <span className={`rm-chip ${device.status === "active" ? "rm-chip--ok" : "rm-chip--off"}`}>
                      {device.status_label || statusLabel(device.status)}
                    </span>
                  </td>
                  <td>
                    <span className={`rm-chip ${device.pairing_status === "paired" ? "rm-chip--ok" : device.pairing_status === "pairing_ready" ? "rm-chip--info" : "rm-chip--off"}`}>
                      {device.pairing_status_label || "Unpaired"}
                    </span>
                    {device.pairing_status === "pairing_ready" ? (
                      <small className="posDeviceMeta">Expires {formatDate(device.pairing_expires_at)}</small>
                    ) : null}
                  </td>
                  <td>{formatDate(device.last_seen_at)}</td>
                  <td>
                    <div className="posDeviceActions">
                      <button type="button" className="rm-topBtn" onClick={() => openEditModal(device)} disabled={actionId === device.id}>
                        <FiEdit2 /> Edit
                      </button>
                      {["guest_order_tablet", "service_display"].includes(device.device_type) && device.status === "active" ? (
                        <button type="button" className="rm-topBtn rm-topBtn--cyan" onClick={() => createPairing(device)} disabled={actionId === device.id}>
                          <FiKey /> {device.pairing_status === "paired" ? "Re-pair" : "Pair"}
                        </button>
                      ) : null}
                      {device.pairing_status === "paired" ? (
                        <button type="button" className="rm-topBtn rm-topBtn--amber" onClick={() => revokeDevice(device)} disabled={actionId === device.id}>
                          <FiSlash /> Revoke
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className={`rm-topBtn ${device.status === "active" ? "rm-topBtn--amber" : "rm-topBtn--green"}`}
                        onClick={() => updateDeviceStatus(device)}
                        disabled={actionId === device.id}
                      >
                        <FiPower /> {device.status === "active" ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!devices.length ? (
                <tr>
                  <td colSpan="8" className="posMuted">No POS devices found.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {modalMode ? (
        <div className="posModalOverlay" role="presentation">
          <div className="posTicketModal posProductModal" role="dialog" aria-modal="true" aria-label="POS device setup">
            <div className="posTicketModalHeader">
              <div>
                <h3 className="rm-rates-title">{modalMode === "edit" ? "Edit device" : "Add device"}</h3>
                <div className="rm-rates-subtitle">Configure a POS tablet, service display or terminal.</div>
              </div>
              <button type="button" className="res-filterCloseBtn" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>

            <div className="posProductModalBody">
              <div className="posModalFormGrid">
                <label className="posModalField posModalField--wide">
                  <span>Name</span>
                  <input
                    className="posTextInput"
                    value={deviceForm.name}
                    onChange={(event) => updateForm("name", event.target.value)}
                    placeholder="Rooftop tablet 1"
                  />
                </label>
                <label className="posModalField">
                  <span>Type</span>
                  <select
                    className="posSelect"
                    value={deviceForm.device_type}
                    onChange={(event) => updateDeviceType(event.target.value)}
                  >
                    {DEVICE_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label className="posModalField">
                  <span>Status</span>
                  <select
                    className="posSelect"
                    value={deviceForm.status}
                    onChange={(event) => updateForm("status", event.target.value)}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                {deviceForm.device_type === "service_display" ? (
                  <div className="posModalField posModalField--wide">
                    <span>Order outlets</span>
                    <div className="posDeviceOutletToolbar">
                      <button
                        type="button"
                        className={`rm-topBtn ${deviceForm.outlet_ids.length === 0 ? "rm-topBtn--cyan" : ""}`}
                        onClick={() => updateForm("outlet_ids", [])}
                      >
                        All outlets
                      </button>
                      <button
                        type="button"
                        className="rm-topBtn"
                        onClick={() => updateForm("outlet_ids", outlets.map((outlet) => String(outlet.id)))}
                      >
                        Select all
                      </button>
                      <span>{deviceForm.outlet_ids.length ? `${deviceForm.outlet_ids.length} selected` : "Every outlet"}</span>
                    </div>
                    <div className="posDeviceOutletPicker">
                      {outlets.map((outlet) => {
                        const selected = deviceForm.outlet_ids.map(String).includes(String(outlet.id));
                        return (
                          <label key={outlet.id} className={`posDeviceOutletOption ${selected ? "posDeviceOutletOption--selected" : ""}`}>
                            <input type="checkbox" checked={selected} onChange={() => toggleDisplayOutlet(outlet.id)} />
                            <span>{outlet.name}</span>
                          </label>
                        );
                      })}
                      {!outlets.length ? <span className="posMuted">No active outlets available.</span> : null}
                    </div>
                  </div>
                ) : (
                  <label className="posModalField">
                    <span>Outlet</span>
                    <select
                      className="posSelect"
                      value={deviceForm.outlet_id}
                      onChange={(event) => updateForm("outlet_id", event.target.value)}
                    >
                      <option value="">No outlet</option>
                      {outlets.map((outlet) => <option key={outlet.id} value={outlet.id}>{outlet.name}</option>)}
                    </select>
                  </label>
                )}
                {deviceForm.device_type === "guest_order_tablet" ? (
                  <label className="posModalField">
                    <span>Catalog</span>
                    <select
                      className="posSelect"
                      value={deviceForm.catalog_id}
                      onChange={(event) => updateCatalog(event.target.value)}
                    >
                      <option value="">No catalog</option>
                      {catalogs.map((catalog) => (
                        <option key={catalog.id} value={catalog.id}>
                          {catalog.outlet_name ? `${catalog.outlet_name} · ` : ""}{catalog.name} ({Number(catalog.product_count || 0).toLocaleString("es-ES")})
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {deviceForm.device_type === "service_display" ? (
                  <div className="posServiceAlertSetup posModalField--wide">
                    <div className="posServiceAlertHeading">
                      <FiBell />
                      <div>
                        <strong>New order alerts</strong>
                        <span>Alerts repeat while orders remain unreceived.</span>
                      </div>
                    </div>
                    <label className="posCatalogToggle">
                      <input
                        type="checkbox"
                        checked={deviceForm.alert_settings.sound_enabled}
                        onChange={(event) => updateAlertSetting("sound_enabled", event.target.checked)}
                      />
                      <span><FiVolume2 /> Sound alert</span>
                    </label>
                    <label className="posCatalogToggle">
                      <input
                        type="checkbox"
                        checked={deviceForm.alert_settings.visual_enabled}
                        onChange={(event) => updateAlertSetting("visual_enabled", event.target.checked)}
                      />
                      <span><FiBell /> Visual alert</span>
                    </label>
                    <label className="posModalField">
                      <span>Tone</span>
                      <select
                        className="posSelect"
                        value={deviceForm.alert_settings.tone}
                        disabled={!deviceForm.alert_settings.sound_enabled}
                        onChange={(event) => updateAlertSetting("tone", event.target.value)}
                      >
                        {ALERT_TONE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    </label>
                    <label className="posModalField">
                      <span>Repeat</span>
                      <select
                        className="posSelect"
                        value={deviceForm.alert_settings.repeat_seconds}
                        disabled={!deviceForm.alert_settings.sound_enabled}
                        onChange={(event) => updateAlertSetting("repeat_seconds", Number(event.target.value))}
                      >
                        {ALERT_REPEAT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    </label>
                    <label className="posModalField posServiceVolumeField">
                      <span>Volume · {deviceForm.alert_settings.volume}%</span>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="5"
                        value={deviceForm.alert_settings.volume}
                        disabled={!deviceForm.alert_settings.sound_enabled}
                        onChange={(event) => updateAlertSetting("volume", Number(event.target.value))}
                      />
                    </label>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="posProductModalFooter">
              <button type="button" className="rm-topBtn" onClick={closeModal} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="rm-topBtn rm-topBtn--green" onClick={saveDevice} disabled={saving}>
                {saving ? "Saving" : "Save device"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {generatedAccess?.accessUrl ? (
        <div className="posModalOverlay" role="presentation">
          <div className="posTicketModal posQrPreviewModal posPairingModal" role="dialog" aria-modal="true" aria-label={`Pair ${generatedAccess.deviceName}`}>
            <div className="posTicketModalHeader">
              <div>
                <h3 className="rm-rates-title">Pair device</h3>
                <div className="rm-rates-subtitle">{generatedAccess.deviceName} · {deviceTypeLabel(generatedAccess.deviceType)}</div>
              </div>
              <button type="button" className="res-filterCloseBtn" onClick={() => setGeneratedAccess(null)} aria-label="Close">×</button>
            </div>
            <div className="posQrPreviewBody">
              <div className="posQrPreviewCode">
                <QRCodeSVG value={generatedAccess.accessUrl} size={280} level="M" includeMargin />
              </div>
              <div className="posQrPreviewDetails posPairingModalDetails">
                <span>One-time pairing</span>
                <div className="posPairingSecurity">
                  <FiShield /> Expires {formatDate(generatedAccess.expiresAt)}
                </div>
                <input className="posTextInput posAccessInput" value={generatedAccess.accessUrl} readOnly aria-label="Pairing URL" />
                <small>Scan this QR from the pairing screen on the device.</small>
              </div>
            </div>
            <div className="posProductModalFooter">
              <button type="button" className="rm-topBtn" onClick={() => setGeneratedAccess(null)}>
                Close
              </button>
              <button type="button" className="rm-topBtn rm-topBtn--cyan" onClick={copyAccessUrl}>
                <FiCopy /> Copy link
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
